/* Convert backend DTOs into the frontend's Replay/Tick shape that chart
   components expect. The backend's GhostInput stream becomes a Tick[] with
   derived fields (velocity magnitude, pitch/yaw/roll split out, etc.). */

import {
  Checkpoint,
  formatTime,
  kmh,
  Replay,
  ReplayMeta,
  Tick,
  Tier,
} from '../model/Replay';
import { ApiParsedReplay, ApiReplaySummary } from './types';

// GBX records RPM as a 0-255 byte. Real engines top out around 8000-ish, so
// scale to keep the RPM chart's 2000-8200 Y-axis usable. Tune later if Nadeo
// turns out to use a different mapping.
const RPM_BYTE_TO_REAL = 8200 / 255;

export function replayFromApi(api: ApiParsedReplay): Replay {
  const inputs = api.replay.inputs || [];
  const durationSec = api.stats?.durationSec ?? (inputs.length ? inputs[inputs.length - 1].timestampMS / 1000 : 0);

  const ticks: Tick[] = inputs.map((inp) => {
    const vx = inp.velocity?.x ?? 0;
    const vy = inp.velocity?.y ?? 0;
    const vz = inp.velocity?.z ?? 0;
    return {
      t: inp.timestampMS / 1000,
      x: inp.position?.x ?? 0,
      y: inp.position?.y ?? 0,
      z: inp.position?.z ?? 0,
      speed: inp.speed ?? 0,
      steer: inp.steer ?? 0,
      gas: inp.gas === true,
      brake: inp.brake === true,
      gear: Math.round(inp.gear ?? 0),
      rpm: (inp.rpm ?? 0) * RPM_BYTE_TO_REAL,
      pitch: inp.pitchYawRoll?.x ?? 0,
      yaw: inp.pitchYawRoll?.y ?? 0,
      roll: inp.pitchYawRoll?.z ?? 0,
      vx, vy, vz,
      vmag: Math.hypot(vx, vy, vz),
    };
  });

  const checkpoints: Checkpoint[] = (api.replay.checkpoints || []).map((cp, i) => {
    const tSec = cp.timeMS / 1000;
    return { t: tSec, sIndex: i, s: durationSec > 0 ? tSec / durationSec : 0 };
  });

  const meta: ReplayMeta = {
    replayUid: String(api.replay.replayUid),
    ghostUid: api.replay.ghostUid,
    challengeUid: api.replay.validate_ChallangeUid || '',
    author: api.replay.authorName || '—',
    location: api.replay.location || '',
    // No human map name in the GBX header; fall back to the challenge UID.
    map: api.replay.validate_ChallangeUid || '—',
    respawns: api.replay.respawns ?? 0,
    tier: api.stats?.tier ?? 'S',
    scanDate: api.scannedAt,
    raceTimeMS: api.replay.raceTimeMS,
    raceTimeStr: formatTime(api.replay.raceTimeMS),
    topSpeed: api.stats?.topSpeedMs ?? 0,
    topSpeedKmh: Math.round(kmh(api.stats?.topSpeedMs ?? 0)),
    avgSpeed: api.stats?.avgSpeedMs ?? 0,
    avgSpeedKmh: Math.round(kmh(api.stats?.avgSpeedMs ?? 0)),
    suspiciousCount: api.stats?.clusterCount ?? 0,
  };

  return {
    meta,
    data: {
      ticks,
      checkpoints,
      duration: durationSec,
      topSpeed: api.stats?.topSpeedMs ?? 0,
      avgSpeed: api.stats?.avgSpeedMs ?? 0,
      bins: api.stats?.bins ?? new Array(41).fill(0),
      exactBins: (api.stats?.exactBins ?? []).map((b) => ({
        value: b.value,
        count: b.count,
        isSuspicious: b.isSuspicious === true,
      })),
      clusters: (api.stats?.clusters ?? []).map((c) => ({
        tStart: c.tStart, tEnd: c.tEnd, value: c.value, count: c.count,
      })),
    },
  };
}

// Lightweight version for leaderboard rows — no ticks, no charts.
export interface ReplaySummary {
  ghostUid: string;
  author: string;
  map: string;
  challengeUid: string;
  location: string;
  raceTimeMS: number;
  raceTimeStr: string;
  topSpeedKmh: number;
  avgSpeedKmh: number;
  tier: Tier;
  clusterCount: number;
  scanDate: string;
}

export function summaryFromApi(s: ApiReplaySummary): ReplaySummary {
  return {
    ghostUid: s.ghostUid,
    author: s.authorName || '—',
    map: s.map || s.challengeUid || '—',
    challengeUid: s.challengeUid || '',
    location: s.location || '',
    raceTimeMS: s.raceTimeMS,
    raceTimeStr: formatTime(s.raceTimeMS),
    topSpeedKmh: Math.round(kmh(s.topSpeedMs)),
    avgSpeedKmh: Math.round(kmh(s.avgSpeedMs)),
    tier: s.tier ?? 'S',
    clusterCount: s.clusterCount,
    scanDate: s.scannedAt,
  };
}
